import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import JukoBoxLogo from '../components/JukoBoxLogo';
import PageTransition from '../components/PageTransition';
import { useAuth } from '../contexts/AuthContext';

const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Vérifier si le token est présent
    if (!token) {
      setError('Token de réinitialisation manquant');
      setIsValidToken(false);
    } else {
      setIsValidToken(true);
    }
    setIsLoading(false);
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Vérifier que les mots de passe correspondent
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    // Vérifier la longueur du mot de passe
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setIsSubmitting(true);

    try {
      if (!token) {
        throw new Error('Token de réinitialisation manquant');
      }

      const result = await resetPassword(token, newPassword);
      if (result.success) {
        setSuccess(true);
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.error || 'Une erreur est survenue');
      }
    } catch (err: any) {
      setError(
        err.message || 'Une erreur est survenue lors de la réinitialisation du mot de passe'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header avec logo */}
        <Box
          sx={{
            py: 4,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
            <JukoBoxLogo size={64} />
          </Container>
        </Box>
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 32px 0 rgba(0,0,0,0.08)',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}
              >
                Réinitialiser le mot de passe
              </Typography>
              {!isValidToken ? (
                <>
                  <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                    {error || 'Token de réinitialisation invalide ou expiré'}
                  </Alert>
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => navigate('/forgot-password')}
                      sx={{ p: 0, minWidth: 'auto', ml: 0.5, boxShadow: 'none' }}
                    >
                      Demander un nouveau lien de réinitialisation
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    sx={{ mb: 4, color: 'text.secondary', textAlign: 'center' }}
                  >
                    Entrez votre nouveau mot de passe
                  </Typography>
                  {error && (
                    <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
                      Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé
                      vers la page de connexion.
                    </Alert>
                  )}
                  <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="newPassword"
                      label="Nouveau mot de passe"
                      type="password"
                      id="newPassword"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isSubmitting || success}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'text.primary',
                          '& fieldset': { borderColor: 'grey.400' },
                          '&:hover fieldset': { borderColor: 'grey.700' },
                          '&.Mui-focused fieldset': { borderColor: 'grey.700' },
                        },
                        '& .MuiInputLabel-root': { color: 'grey.600' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'grey.800' },
                        '& .MuiInputLabel-root.MuiFormLabel-filled': { color: 'grey.800' },
                      }}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="confirmPassword"
                      label="Confirmer le mot de passe"
                      type="password"
                      id="confirmPassword"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isSubmitting || success}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'text.primary',
                          '& fieldset': { borderColor: 'grey.400' },
                          '&:hover fieldset': { borderColor: 'grey.700' },
                          '&.Mui-focused fieldset': { borderColor: 'grey.700' },
                        },
                        '& .MuiInputLabel-root': { color: 'grey.600' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'grey.800' },
                        '& .MuiInputLabel-root.MuiFormLabel-filled': { color: 'grey.800' },
                      }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 3,
                        mb: 2,
                        borderRadius: '50px',
                        py: 1.5,
                        fontSize: '1.1rem',
                        boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                        '&:hover': {
                          boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                        },
                      }}
                      disabled={isSubmitting || success}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Réinitialiser le mot de passe'
                      )}
                    </Button>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => navigate('/login')}
                        sx={{ p: 0, minWidth: 'auto', ml: 0.5, boxShadow: 'none' }}
                      >
                        Retour à la connexion
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Paper>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default ResetPassword;
